import { __decorate } from "tslib";
import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { autoUpdate, computePosition, flip, offset, shift, } from '@floating-ui/dom';
import { Placement } from './enums/placement.js';
import { Trigger } from './enums/trigger.js';
import { Size } from './enums/size.js';
import './viedoc-tooltip-content.js';
/* eslint-enable import/no-duplicates */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { Large, ...TooltipSize } = Size;
export { TooltipSize };
const HOVER_DELAY = 250; // ms
const ANIMATION_DURATION = 250; // ms
const POSITION_STRATEGY = 'fixed';
const TOOLTIP_OFFSET = 8; // px
let ViedocTooltip = class ViedocTooltip extends LitElement {
    constructor() {
        super(...arguments);
        this.label = '';
        this.open = false;
        this.placement = Placement.Bottom;
        this.size = TooltipSize.Medium;
        this.trigger = `${Trigger.Hover} ${Trigger.Focus}`;
        this.containerId = '';
        this.referenceElement = null;
        this.tooltipContent = null;
        this.hoverTimer = 0;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.clearTimeout(this.hoverTimer);
        if (this.tooltipContent) {
            this.tooltipContent.remove();
            this.tooltipContent = null;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('open')) {
            if (this.open) {
                if (!this.tooltipContent) {
                    this.tooltipContent = this.createTooltipContent();
                }
                this.fadeIn();
            }
            else {
                this.fadeOut(() => this.handleFadeOutAnimationFinish());
            }
        }
    }
    handleFadeOutAnimationFinish() {
        if (this.cleanup) {
            this.cleanup();
        }
        if (this.tooltipContent) {
            this.tooltipContent.remove();
            this.tooltipContent = null;
        }
    }
    hasTrigger(name) {
        const triggers = this.trigger.split(' ');
        return triggers.includes(name);
    }
    handleClick() {
        if (this.hasTrigger(Trigger.Click)) {
            if (this.open) {
                this.hide();
            }
            else {
                this.show();
            }
        }
    }
    handleFocusOut() {
        if (this.hasTrigger(Trigger.Focus)) {
            this.hide();
        }
    }
    handleFocusIn() {
        if (this.hasTrigger(Trigger.Focus)) {
            this.show();
        }
    }
    handleKeyDown(evt) {
        if (this.open && evt.key === 'Escape') {
            evt.stopPropagation();
            this.hide();
        }
    }
    handleMouseOver() {
        if (this.hasTrigger(Trigger.Hover)) {
            this.hoverTimer = window.setTimeout(() => {
                this.show();
            }, HOVER_DELAY);
        }
    }
    handleMouseOut() {
        if (this.hasTrigger(Trigger.Hover)) {
            window.clearTimeout(this.hoverTimer);
            this.hide();
        }
    }
    updateReferenceElement() {
        // Find first element in the default slot. This will be used to anchor the tooltip.
        this.referenceElement = this.defaultSlot.assignedElements({
            flatten: true,
        })[0];
        if (!this.referenceElement) {
            return;
        }
        this.referenceElement.setAttribute('aria-describedby', 'tooltip');
    }
    // Updates the position of the tooltip using floating-ui's computePosition function.
    async updateTooltipPosition() {
        if (!this.referenceElement || !this.tooltipContent) {
            return;
        }
        const { x, y } = await computePosition(this.referenceElement, this.tooltipContent, {
            strategy: POSITION_STRATEGY,
            placement: this.placement,
            middleware: [
                offset({ mainAxis: TOOLTIP_OFFSET, alignmentAxis: 0 }),
                flip({ padding: 6 }),
                shift({ padding: 6 }),
            ],
        });
        Object.assign(this.tooltipContent.style, {
            left: `${x}px`,
            top: `${y}px`,
        });
    }
    // Sets the initial opacity and position of the tooltip and
    // propagates CSS custom properties from the host element to the tooltip.
    setTooltipContentStyles(tooltip) {
        const style = getComputedStyle(this);
        const maxWidth = style.getPropertyValue('--max-width');
        const tooltipZIndex = style.getPropertyValue('--tooltip-z-index');
        tooltip.style.setProperty('position', POSITION_STRATEGY);
        tooltip.style.setProperty('opacity', '0');
        tooltip.style.setProperty('max-width', maxWidth);
        tooltip.style.setProperty('z-index', tooltipZIndex);
    }
    // Copies the nodes of the content slot to the tooltip content element.
    copySlottedContent(tooltip) {
        if (this.contentSlot) {
            const content = this.contentSlot.assignedNodes();
            content.forEach(node => {
                const clone = node.cloneNode(true);
                tooltip.appendChild(clone);
            });
        }
    }
    // Appends tooltip to the container specified by the container attribute.
    // If no container is specified, the tooltip is appended to the shadow root.
    appendToContainerElement(tooltip) {
        const container = this.containerId
            ? document.getElementById(this.containerId)
            : this.shadowRoot;
        if (container) {
            container.appendChild(tooltip);
        }
    }
    createTooltipContent() {
        const tooltip = document.createElement('viedoc-tooltip-content');
        tooltip.size = this.size;
        tooltip.id = 'tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.setAttribute('aria-label', this.label);
        this.setTooltipContentStyles(tooltip);
        this.copySlottedContent(tooltip);
        this.appendToContainerElement(tooltip);
        // When open is set already on first render slot change event has not been fired yet.
        // This means that the reference element is not set yet and we need to set it before positioning the tooltip.
        if (!this.referenceElement) {
            this.updateReferenceElement();
        }
        if (this.referenceElement) {
            // Auto update ensures that element remains anchored when scrolling or resizing the screen
            this.cleanup = autoUpdate(this.referenceElement, tooltip, () => this.updateTooltipPosition());
        }
        return tooltip;
    }
    pausExistingAnimations() {
        if (this.tooltipContent) {
            this.tooltipContent
                .getAnimations()
                .forEach(animation => animation.pause());
        }
    }
    fadeIn(onFinish) {
        if (this.tooltipContent) {
            this.pausExistingAnimations();
            const animation = this.tooltipContent.animate([{ opacity: 1 }], {
                duration: ANIMATION_DURATION,
                fill: 'forwards',
            });
            if (onFinish) {
                animation.onfinish = onFinish;
            }
        }
    }
    fadeOut(onFinish) {
        if (this.tooltipContent) {
            this.pausExistingAnimations();
            const animation = this.tooltipContent.animate([{ opacity: 0 }], {
                duration: ANIMATION_DURATION,
                fill: 'forwards',
            });
            if (onFinish) {
                animation.onfinish = onFinish;
            }
        }
    }
    show() {
        this.open = true;
    }
    hide() {
        this.open = false;
    }
    render() {
        // Note: We need to disable this rule because we are not using the focus event that the rule requires.
        // Instead we are using the focusin and focusout events which bubbles.
        // eslint-disable-next-line lit-a11y/mouse-events-have-key-events
        return html `
      <slot name="content"></slot>
      <slot
        @click=${this.handleClick}
        @focusin=${this.handleFocusIn}
        @focusout=${this.handleFocusOut}
        @keydown=${this.handleKeyDown}
        @mouseover=${this.handleMouseOver}
        @mouseout=${this.handleMouseOut}
        @slotchange=${this.updateReferenceElement}
      ></slot>
    `;
    }
};
ViedocTooltip.styles = [
    css `
      :host {
        --tooltip-z-index: 4;
        --max-width: 20rem;

        display: inline;
      }

      ::slotted([slot='content']) {
        display: none;
      }
    `,
];
__decorate([
    property({ type: String })
], ViedocTooltip.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocTooltip.prototype, "open", void 0);
__decorate([
    property({ type: String })
], ViedocTooltip.prototype, "placement", void 0);
__decorate([
    property({ type: String })
], ViedocTooltip.prototype, "size", void 0);
__decorate([
    property({ type: String })
], ViedocTooltip.prototype, "trigger", void 0);
__decorate([
    property({ type: String, attribute: 'container' })
], ViedocTooltip.prototype, "containerId", void 0);
__decorate([
    query('slot:not([name])')
], ViedocTooltip.prototype, "defaultSlot", void 0);
__decorate([
    query('slot[name="content"]')
], ViedocTooltip.prototype, "contentSlot", void 0);
ViedocTooltip = __decorate([
    customElement('viedoc-tooltip')
], ViedocTooltip);
export { ViedocTooltip };
